import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, MessageCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openChat } from "@/lib/chat";

const DEFAULT_STORAGE_KEY = "sideb:exit-intent-shown";
const MIN_DWELL_MS = 7000;

/**
 * Exit-intent popup. Configurable via props so the same component can serve
 * multiple pages with different copy + storage keys.
 *
 * Triggers:
 *   - Desktop: mouseleave near the top edge of the viewport
 *   - Mobile: document hidden (tab switch / app switch) OR popstate (back button)
 *
 * Once per session per storageKey. Won't fire until MIN_DWELL_MS has elapsed
 * so we don't ambush visitors the moment they land.
 *
 * Props:
 *   - enabled: boolean — turn the trigger off (e.g. on inner Parse steps)
 *   - storageKey: string — sessionStorage key; one per surface so Services
 *       and Parse don't share state
 *   - eyebrow / title / description — copy
 *   - primaryHref / primaryLabel — main CTA (Link target)
 *   - primaryExternal — open primaryHref in new tab
 *   - chatContext — context string passed to openChat()
 *   - secondaryLabel — chat button label
 *   - dismissLabel — bottom dismiss link copy
 */
export default function ExitIntentPopup({
  enabled = true,
  storageKey = DEFAULT_STORAGE_KEY,
  eyebrow = "Off menu",
  title = "Don't see what you need?",
  description = "We scope custom projects too. Tell us what you're working on and we'll come back with the right shape for the work.",
  primaryHref = "/inquiry",
  primaryLabel = "Submit a brief",
  primaryExternal = false,
  primaryIcon = "file",
  chatContext = "Exit intent · Custom project inquiry",
  secondaryLabel = "Chat with us",
  dismissLabel = "No thanks, keep browsing",
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(storageKey) === "1") return;

    const landedAt = Date.now();
    let armed = false;

    const armTimer = window.setTimeout(() => {
      armed = true;
    }, MIN_DWELL_MS);

    const trigger = () => {
      if (!armed) return;
      if (window.sessionStorage.getItem(storageKey) === "1") return;
      window.sessionStorage.setItem(storageKey, "1");
      setOpen(true);
    };

    const onMouseOut = (e) => {
      if (e.relatedTarget) return;
      if (e.clientY > 8) return;
      trigger();
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden" && Date.now() - landedAt > MIN_DWELL_MS) {
        trigger();
      }
    };

    const onPopState = () => {
      trigger();
    };

    document.addEventListener("mouseout", onMouseOut);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("popstate", onPopState);

    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("popstate", onPopState);
    };
  }, [enabled, storageKey]);

  if (!open) return null;

  const close = () => setOpen(false);

  const handleChat = () => {
    close();
    openChat({ context: chatContext });
  };

  const PrimaryIcon = primaryIcon === "calendar" ? FileText : FileText; // placeholder swap point if needed

  const PrimaryButton = primaryExternal ? (
    <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-5 justify-center" data-testid="exit-intent-brief-cta">
      <a href={primaryHref} target="_blank" rel="noopener noreferrer" onClick={close}>
        <PrimaryIcon className="mr-2 h-4 w-4" />
        {primaryLabel}
        <ArrowRight className="ml-auto h-4 w-4" />
      </a>
    </Button>
  ) : (
    <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-5 justify-center" data-testid="exit-intent-brief-cta">
      <Link to={primaryHref} onClick={close}>
        <PrimaryIcon className="mr-2 h-4 w-4" />
        {primaryLabel}
        <ArrowRight className="ml-auto h-4 w-4" />
      </Link>
    </Button>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      data-testid="exit-intent-popup"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="absolute inset-0 bg-[#0b1e5a]/60 backdrop-blur-sm" aria-hidden onClick={close} />
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(11,30,90,0.45)] border border-border p-6 sm:p-8 animate-in fade-in zoom-in-95 duration-200"
        data-testid="exit-intent-card"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="exit-intent-close"
          className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-muted hover:bg-foreground/10 text-foreground/70 hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="label-mono text-[#ac62ea]">{eyebrow}</div>
        <h2 id="exit-intent-title" className="font-display text-2xl sm:text-3xl mt-2 font-semibold tracking-[-0.025em] leading-snug">
          {title}
        </h2>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
          {description}
        </p>

        <div className="mt-6 flex flex-col gap-2.5">
          {PrimaryButton}
          <button
            type="button"
            onClick={handleChat}
            data-testid="exit-intent-chat-cta"
            className="btn-pill border border-border bg-white hover:bg-muted text-foreground h-11 px-5 inline-flex items-center justify-center text-sm font-medium transition-colors"
          >
            <MessageCircle className="mr-2 h-4 w-4 text-[#ac62ea]" />
            {secondaryLabel}
          </button>
        </div>

        <button
          type="button"
          onClick={close}
          className="mt-5 text-xs text-foreground/55 hover:text-foreground/80 transition-colors w-full text-center"
          data-testid="exit-intent-dismiss"
        >
          {dismissLabel}
        </button>
      </div>
    </div>
  );
}
